import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/il-libro", label: "Il Libro" },
  { to: "/corsi", label: "Corsi" },
  { to: "/podcast", label: "Podcast" },
  { to: "/chi-sono", label: "Chi sono" },
  { to: "/storie", label: "Blog" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled ? "bg-nero/95 backdrop-blur-md shadow-lg shadow-black/20" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="shrink-0">
          <img src="/assets/logo-dark.png" alt="Dentro il Calice — L'Influexpert del Vino" className="h-14 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-300 ${
                  isActive ? "text-oro" : "text-panna/80 hover:text-oro"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/#guida-gratuita"
            data-testid="nav-cta-guida"
            className="ml-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm font-medium text-panna transition-colors duration-300 hover:bg-bordeaux-dark border border-oro/30"
          >
            Scarica la guida gratuita
          </Link>
        </nav>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-panna p-2"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-nero border-t border-oro/20"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `font-serif text-xl ${isActive ? "text-oro" : "text-panna"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/#guida-gratuita"
                className="mt-2 rounded-full bg-bordeaux px-5 py-3 text-center font-medium text-panna"
              >
                Scarica la guida gratuita
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
